/**
 * types.ts - Tipos e Interfaces para Validación Cruzada
 * 
 * Módulo extraído de crossValidation.ts (TD-002.G.1)
 * Contiene todas las interfaces, tipos y clases legacy del sistema
 * de validación cruzada multi-fuente.
 * 
 * @created 2026-01-26
 */

import type { GeocodingLevel } from '../../types/errors';
import type { GeocodingSource as CanonicalGeocodingSource } from '../../types/errors/GeocodingErrors';

// ============================================================================
// CONSTANTES EXPORTADAS
// ============================================================================

/** Timeout por defecto para cada fuente (ms) */
export const DEFAULT_SOURCE_TIMEOUT_MS = 5000;

/** Default level para errores de cross-validation */
export const DEFAULT_CROSS_VALIDATION_LEVEL: GeocodingLevel = 'L3';

// ============================================================================
// CLASES LEGACY PARA COMPATIBILIDAD (uso interno)
// ============================================================================

/**
 * Error de timeout legacy para compatibilidad con código existente.
 * @internal
 */
export class LegacyTimeoutError extends Error {
  constructor(
    message: string,
    public readonly timeoutMs: number,
    public readonly context?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Error de no resultados legacy para compatibilidad con código existente.
 * @internal
 */
export class LegacyNoResultsError extends Error {
  constructor(
    message: string,
    public readonly sources: string[],
    public readonly context?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'NoResultsError';
  }
}

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Punto en coordenadas UTM30 ETRS89 (EPSG:25830) */
export interface UTMPoint {
  x: number;  // Easting (metros)
  y: number;  // Northing (metros)
}

/**
 * Identificadores de fuentes de geocodificación.
 * Alias directo del tipo canónico en GeocodingErrors.ts (lowercase).
 * @see CanonicalGeocodingSource
 */
export type GeocodingSourceId = CanonicalGeocodingSource;

/** Query de geocodificación */
export interface GeocodingQuery {
  /** Nombre de la infraestructura */
  nombre: string;
  /** Código INE del municipio (5 dígitos) */
  codMun: string;
  /** Nombre del municipio */
  municipio?: string;
  /** Tipología PTEL */
  tipologia?: string;
  /** Dirección si está disponible */
  direccion?: string;
}

/** Resultado de una geocodificación individual */
export interface GeocodingResult {
  /** Coordenadas UTM30 */
  coordinates: UTMPoint;
  /** Nombre encontrado (puede diferir del query) */
  matchedName?: string;
  /** Score de match del nombre (0-100) */
  matchScore?: number;
  /** Confianza de la fuente (0-1) */
  confidence?: number;
}

/** Configuración de una fuente de geocodificación */
export interface GeocodingSource {
  id: GeocodingSourceId;
  name: string;
  /** Peso de autoridad (0-1): LOCAL=0.95, WFS=0.85, CARTO=0.80, NOM=0.55 */
  authorityWeight: number;
  /** Timeout individual en ms (default 5000) */
  timeoutMs?: number;
  /** Función que ejecuta la geocodificación */
  geocode: (query: GeocodingQuery) => Promise<GeocodingResult | null>;
}

/** Resultado de una fuente con metadatos */
export interface SourceResult {
  /** Identificador de la fuente */
  sourceId: GeocodingSourceId;
  /** Nombre legible de la fuente */
  sourceName: string;
  /** Peso de autoridad de la fuente */
  authorityWeight: number;
  /** Resultado si tuvo éxito */
  result: GeocodingResult | null;
  /** Error tipado si falló (nuevo sistema F023 discriminated unions) */
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports -- import() inline evita dep circular entre crossValidation y types/errors
  typedError?: import('../../types/errors').GeocodingError | null;
  /** Error legacy si falló (compatibilidad con Error class) */
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  error: Error | import('../../types/errors').GeocodingError | null;
  /** Acción recomendada según el error (nuevo sistema F023) */
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  recommendedAction?: import('../../types/errors').RecommendedAction | null;
  /** Tiempo de respuesta en ms */
  responseTimeMs: number;
  /** Estado de la consulta */
  status: 'success' | 'error' | 'timeout' | 'no_results';
}

/** Resultado de análisis de cluster */
export interface ClusterAnalysis {
  /** Punto central calculado (centroide robusto) */
  centroid: UTMPoint;
  /** Radio del cluster en metros */
  radiusMeters: number;
  /** Número de fuentes que concuerdan */
  concordantSources: number;
  /** Fuentes outlier (discrepantes) */
  outlierSources: GeocodingSourceId[];
  /** Score de concordancia (0-1) */
  concordanceScore: number;
}

/** Resultado completo de validación cruzada */
export interface CrossValidationResult {
  /** Query original */
  query: GeocodingQuery;
  /** Resultados de todas las fuentes consultadas */
  sourceResults: SourceResult[];
  /** Análisis del cluster de resultados */
  clusterAnalysis: ClusterAnalysis | null;
  /** Score compuesto final (0-100) */
  compositeScore: number;
  /** Coordenada final recomendada */
  recommendedCoordinate: UTMPoint | null;
  /** Recomendación de acción */
  recommendation: 'USE_RESULT' | 'MANUAL_REVIEW' | 'REJECT';
  /** Razón de la recomendación */
  recommendationReason: string;
  /** Timestamp de la validación */
  timestamp: Date;
}
